import { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import axios from "axios";
import CustomTableWithPagination from "../../components/customTable/CustomTable";
import RefreshIcon from "@mui/icons-material/Refresh";
import MemberPointsDetailsPage from "./MemberPointsDetailsPage";
import { getFormattedDate } from "../../Utils/commonFunction";

const apiEnpoint: any = process.env.REACT_APP_API_BASE_URL;

interface MemberPointsTabProps {
  userId: string;
}

interface PointsData {
  PatronId: number;
  MerchantId: number;
  EstablishmentName: string;
  TotalPoints: string;
  LastPointCreated: string;
}

const MemberPointsTab = ({ userId }: MemberPointsTabProps) => {
  const [loading, setLoading] = useState(false);
  // Local error state not displayed in UI; omitting to avoid unused variable warnings
  const [points, setPoints] = useState<PointsData[]>([]);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<PointsData | null>(
    null
  );

  const columns = [
    {
      id: "EstablishmentName",
      label: "Establishment Name",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "TotalPoints",
      label: "Total Points",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "LastPointCreated",
      label: "Last Point Created",
      minWidth: 200,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    }
  ];

  const fetchPoints = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    
    const token = localStorage.getItem("businessAccessToken");
    const currentBusiness: any = localStorage?.getItem("currentBusiness");
    const business = currentBusiness ? JSON.parse(currentBusiness) : null;
    axios
      .get(
        `${apiEnpoint}/loyaltyprogram/businessportal/pointsummary/user/${userId}/business/${business?.BusinessId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      )
      .then((res) => {
        // API now returns direct array instead of { success, data }
        setPoints(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        setPoints([]);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    fetchPoints();
  }, [userId, fetchPoints]);

  const handleShowDetails = (merchant: PointsData) => {
    setSelectedMerchant(merchant);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedMerchant(null);
  };

  return (
    <Box>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%"
          }}
        >
          <RefreshIcon onClick={() => fetchPoints()} />
        </div>
      </div>
      <CustomTableWithPagination
        columns={columns}
        data={points || []}
        apiStatus={loading ? "fetching" : "idle"}
        isShowPagination={false}
        noDataFoundMessage="No data found."
        handleRowClick={handleShowDetails}
        sx={{ cursor: "pointer" }}
      />
      {selectedMerchant && (
        <MemberPointsDetailsPage
          open={detailsDialogOpen}
          onClose={handleCloseDetails}
          userId={userId}
          businessId={selectedMerchant.MerchantId.toString()}
          establishmentName={selectedMerchant.EstablishmentName}
        />
      )}
    </Box>
  );
};

export default MemberPointsTab;
